<template>
    <v-row v-if="model.value">
        <v-col v-for="(item, index) in items" :key="item.id" cols="12" class="mb-4">
            <v-card outlined>
                <v-card-title>
                    Profissional {{ index + 1 }}
                </v-card-title>
                <v-card-text>
                    <v-row align="center">
                        <v-col cols="12" lg="4" xl="3">
                            <r-image-picker v-model="model.value[index].profile_image_url" :height="280" outlined hide-pixibay label="Foto do profissional" :disabled="readonly" />
                            <div v-if="getFieldError(index, 'profile_image_url')" class="v-messages theme--light error--text mt-1" role="alert">
                                <div class="v-messages__wrapper">
                                    <div class="v-messages__message">{{ getFieldError(index, 'profile_image_url') }}</div>
                                </div>
                            </div>
                        </v-col>
                        <v-col cols="12">
                            <v-text-field v-model="model.value[index].name" label="Nome" placeholder="Nome do profissional" outlined :error-messages="getFieldError(index, 'name')" :readonly="readonly" />
                        </v-col>

                        <v-col cols="12">
                            <v-text-field v-model="model.value[index].registration_number" label="Número de registro" placeholder="Ex: CRM 12345" outlined :error-messages="getFieldError(index, 'registration_number')" :readonly="readonly" />
                        </v-col>

                        <v-col cols="12">
                            <v-text-field v-model="model.value[index].specialty" label="Especialidade/Áreas de atuação" placeholder="Ex: Cardiologia, Clínica Geral" outlined :error-messages="getFieldError(index, 'specialty')" :readonly="readonly" />
                        </v-col>

                        <v-col cols="12">
                            <v-textarea
                                v-model="model.value[index].mini_bio"
                                label="Mini bio (100 a 160 caracteres)"
                                placeholder="Ex: Médico cardiologista com mais de 15 anos de experiência. Especialista em arritmias e insuficiência cardíaca. Atua com foco em prevenção e qualidade de vida."
                                outlined
                                :error-messages="getFieldError(index, 'mini_bio')"
                                :readonly="readonly"
                                :maxlength="160"
                                rows="4"
                            />
                            <div v-if="!readonly" class="mini-bio-counter mt-1">
                                <v-progress-linear
                                    :value="getMiniBioProgress(index)"
                                    :color="getMiniBioColor(index)"
                                    height="10"
                                    rounded
                                    background-opacity="0.15"
                                    class="mb-1"
                                />
                                <div class="text-caption" :class="getMiniBioColor(index) + '--text'">
                                    {{ getMiniBioHint(index) }}
                                </div>
                            </div>
                        </v-col>

                        <v-col cols="12">
                            <v-checkbox v-model="model.value[index].profile_image_later" label="Ainda não tenho foto do profissional e vou enviar depois" hide-details :readonly="readonly" />
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-card-actions>
                    <v-btn v-if="!readonly" @click="removeItem(index)" color="error" tabindex="-1">
                        Remover
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-col>

        <v-col v-if="!readonly" cols="12" :class="!items.length ? 'text-center' : ''">
            <v-btn color="primary" @click="addItem" :disabled="items.length >= 10">
                Adicionar profissional
            </v-btn>
        </v-col>
    </v-row>
</template>

<script>
export default {
    props: {
        value: {
            type: Object,
            default: () => ({ value: null })
        },
        question: {
            type: Object,
            required: true
        },
        errors: {
            type: Array,
            default: () => []
        },
        readonly: {
            type: Boolean,
            default: false
        }
    },
    inject: ['fieldValidationsFunctions'],
    data: () => ({
        validationErrors: []
    }),
    computed: {
        model: {
            get() {
                return this.value
            },
            set(value) {
                this.$emit('input', value)
            }
        },
        items() {
            if (!Array.isArray(this.model.value)) return []

            return this.model.value.map((item, index) => ({
                id: index,
                name: item.name,
                registration_number: item.registration_number,
                specialty: item.specialty,
                profile_image_url: item.profile_image_url,
                profile_image_later: item.profile_image_later,
                mini_bio: item.mini_bio,
            }))
        },
    },
    methods: {
        addItem() {
            this.model.value.push({
                name: '',
                registration_number: '',
                specialty: '',
                profile_image_url: '',
                profile_image_later: false,
                mini_bio: ''
            })
        },
        removeItem(index) {
            this.model.value.splice(index, 1)
        },
        getMiniBioLength(index) {
            return (this.model.value[index].mini_bio || '').length
        },
        getMiniBioProgress(index) {
            return Math.min(100, Math.round((this.getMiniBioLength(index) / 100) * 100))
        },
        getMiniBioColor(index) {
            const len = this.getMiniBioLength(index)
            if (len === 0) return 'grey'
            if (len < 50) return 'error'
            if (len < 100) return 'warning'
            if (len <= 160) return 'success'
            return 'error'
        },
        getMiniBioHint(index) {
            const len = this.getMiniBioLength(index)
            if (len === 0) return 'Comece a escrever — ' + len + '/160 caracteres (mínimo 100)'
            if (len < 50) return 'Continue escrevendo... — ' + len + '/160 caracteres (mínimo 100)'
            if (len < 80) return 'Quase lá! — ' + len + '/160 caracteres (mínimo 100)'
            if (len < 100) return 'Só mais um pouco! — ' + len + '/160 caracteres (mínimo 100)'
            if (len <= 140) return 'Muito bom! ✓ — ' + len + '/160 caracteres'
            if (len <= 160) return 'Excelente! ✓ — ' + len + '/160 caracteres'
            return 'Texto muito longo — ' + len + '/160 caracteres (máximo 160)'
        },
        getFieldError(index, field) {
            if (!this.validationErrors[index]) return ''
            return this.validationErrors[index][field] || ''
        },
        validate() {
            if (this.readonly) {
                this.validationErrors = []
                return []
            }

            const items = this.model.value
            if (!Array.isArray(items)) {
                this.validationErrors = []
                return []
            }

            const allErrors = []
            this.validationErrors = items.map((item, index) => {
                const errors = {}

                if (!item.name || !item.name.trim()) {
                    errors.name = 'Campo obrigatório'
                    allErrors.push(`Profissional ${index + 1}: Nome é obrigatório`)
                }

                if (!item.registration_number || !item.registration_number.trim()) {
                    errors.registration_number = 'Campo obrigatório'
                    allErrors.push(`Profissional ${index + 1}: Número de registro é obrigatório`)
                }

                if (!item.specialty || !item.specialty.trim()) {
                    errors.specialty = 'Campo obrigatório'
                    allErrors.push(`Profissional ${index + 1}: Especialidade é obrigatória`)
                }

                if (!item.mini_bio || !item.mini_bio.trim()) {
                    errors.mini_bio = 'Campo obrigatório (mínimo 100 caracteres)'
                    allErrors.push(`Profissional ${index + 1}: Mini bio é obrigatória`)
                } else if (item.mini_bio.trim().length < 100) {
                    errors.mini_bio = `Mínimo de 100 caracteres (atual: ${item.mini_bio.trim().length})`
                    allErrors.push(`Profissional ${index + 1}: Mini bio deve ter no mínimo 100 caracteres`)
                }

                if (!item.profile_image_url && !item.profile_image_later) {
                    errors.profile_image_url = 'Envie uma foto ou marque a opção de enviar depois'
                    allErrors.push(`Profissional ${index + 1}: Foto é obrigatória`)
                }

                return errors
            })

            return allErrors
        }
    },
    mounted() {
        if (!Array.isArray(this.model.value) || !this.model.value) {
            this.model.value = []
        }

        this.fieldValidationsFunctions.push(this.validate)
    },
    destroyed() {
        const index = this.fieldValidationsFunctions.indexOf(this.validate)
        if (index > -1) {
            this.fieldValidationsFunctions.splice(index, 1)
        }
    },
}
</script>

<style scoped>
.mini-bio-counter {
    padding: 0 12px;
}
</style>